import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostBinding,
  OnDestroy,
  Renderer2,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { IDevicePicture } from '@app/api/interfaces/device-picture.interface';
import { PictureRotation } from '@app/core/enums/picture-rotation.enum';

interface ISize {
  width: number;
  height: number;
}

@Directive({
  selector: '[gcPictureRotationSize]',
  standalone: true,
})
export class PictureRotationSizeDirective implements AfterViewInit, OnDestroy {
  public gcPictureRotationSize = input<IDevicePicture | null>(null);

  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);

  private parent: HTMLElement | null = null;
  private image: HTMLImageElement | null = null;

  private parentSize = signal<ISize>({ width: 0, height: 0 });

  private naturalSize = signal<ISize>({ width: 0, height: 0 });

  private isSwapped = computed(() => {
    const rotation = this.gcPictureRotationSize()?.rotation;

    return rotation === PictureRotation.Deg90 || rotation === PictureRotation.Deg270;
  });

  private containerSize = computed<ISize>(() => {
    const parent = this.parentSize();
    const natural = this.naturalSize();

    if (!parent.width || !parent.height || !natural.width || !natural.height) {
      return { width: 0, height: 0 };
    }

    const targetWidth = this.isSwapped() ? natural.height : natural.width;
    const targetHeight = this.isSwapped() ? natural.width : natural.height;

    const scale = Math.min(parent.width / targetWidth, parent.height / targetHeight);

    return { width: targetWidth * scale, height: targetHeight * scale };
  });

  @HostBinding('style.width.px')
  public get width(): number | null {
    return this.containerSize().width || null;
  }

  @HostBinding('style.height.px')
  public get height(): number | null {
    return this.containerSize().height || null;
  }

  private resizeObserver = new ResizeObserver(() => this.updateParentSize());

  constructor() {
    effect(() => {
      this.gcPictureRotationSize();

      this.recalculate();
    });

    effect(() => this.applyImageSize(this.containerSize(), this.isSwapped()));
  }

  public ngAfterViewInit(): void {
    this.parent = this.el.nativeElement.parentElement;
    this.image = this.el.nativeElement.querySelector('img');

    if (this.parent) {
      this.updateParentSize();
      this.resizeObserver.observe(this.parent);
    }

    if (this.image) {
      this.trackImageLoad(this.image);
    }
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  private recalculate(): void {
    this.updateParentSize();

    if (this.image) {
      this.trackImageLoad(this.image);
    }
  }

  private trackImageLoad(image: HTMLImageElement): void {
    if (image.complete && image.naturalWidth) {
      this.updateNaturalSize(image);
      return;
    }

    image.addEventListener('load', () => this.updateNaturalSize(image), { once: true });
  }

  private updateParentSize(): void {
    if (!this.parent) return;

    this.parentSize.set({ width: this.parent.clientWidth, height: this.parent.clientHeight });
  }

  private updateNaturalSize(image: HTMLImageElement): void {
    this.naturalSize.set({ width: image.naturalWidth, height: image.naturalHeight });
  }

  private applyImageSize(container: ISize, swapped: boolean): void {
    if (!this.image || !container.width || !container.height) return;

    const width = swapped ? container.height : container.width;
    const height = swapped ? container.width : container.height;

    this.renderer.setStyle(this.image, 'width', `${width}px`);
    this.renderer.setStyle(this.image, 'height', `${height}px`);
    this.renderer.setStyle(this.image, 'min-width', `${width}px`);
    this.renderer.setStyle(this.image, 'min-height', `${height}px`);
  }
}
